import type { StreamEvent } from '@/types/streaming'
import { useAuthStore } from '@/stores/authStore'

const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * Parse SSE data lines from a ReadableStream.
 * Uses fetch + ReadableStream (not EventSource) because we need POST with body.
 */
export async function* streamChat(
  message: string,
  sessionId: string | null,
  token: string,
  signal?: AbortSignal,
  context?: Record<string, unknown>,
): AsyncGenerator<StreamEvent> {
  // Pre-flight: if the in-memory token is gone (e.g. page was backgrounded),
  // attempt a silent refresh before opening the SSE stream
  let activeToken = token
  if (!activeToken) {
    const refreshed = await useAuthStore.getState().refreshAccessToken()
    if (refreshed) {
      activeToken = useAuthStore.getState().token!
    } else {
      throw new Error('Session expired — please log in again')
    }
  }

  const response = await fetch(`${API_BASE}/api/v1/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${activeToken}`,
    },
    body: JSON.stringify({
      message,
      channel: 'pwa',
      session_id: sessionId || undefined,
      ...(context && Object.keys(context).length > 0 && { context }),
    }),
    signal,
  })

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Process complete SSE lines
      const lines = buffer.split('\n')
      buffer = lines.pop() || '' // Keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed || !trimmed.startsWith('data: ')) continue

        const jsonStr = trimmed.slice(6) // Remove "data: " prefix
        try {
          const event = JSON.parse(jsonStr) as StreamEvent
          yield event
        } catch {
          // Skip malformed JSON lines
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim().startsWith('data: ')) {
      try {
        const event = JSON.parse(buffer.trim().slice(6)) as StreamEvent
        yield event
      } catch {
        // Skip
      }
    }
  } finally {
    reader.releaseLock()
  }
}
